import React, { useState, useEffect } from 'react';
import { DocumentTextIcon } from '@heroicons/react/24/outline';
import MLErrorClassification from './MLErrorClassification';

export default function SummaryCard({ uploadedFile, selectedTable, universePeriod, validationResult }) {
  const totalRows = validationResult?.universe?.length || 0;

  const totalIssues = validationResult?.universe?.reduce((count, row) => {
    const fieldLevelFields = new Set(
      Object.entries(row.data)
        .filter(([_, cell]) => cell.issue !== null)
        .map(([field]) => field)
    );

    const uniqueRowIssues = row.issues
      ? row.issues.filter(issue => !fieldLevelFields.has(issue.field))
      : [];

    return count + fieldLevelFields.size + uniqueRowIssues.length;
  }, 0);

  const selectedCategory = validationResult?.selectedCategory;

  // Always default to {} so Object.entries never crashes
  const categoryCounts =
    validationResult?.categoryCounts ||
    (validationResult?.universe
      ? validationResult.universe.reduce((acc, row) => {
          const fieldLevelFields = new Set();

          Object.entries(row.data).forEach(([field, cell]) => {
            if (cell.category && cell.issue !== null) {
              fieldLevelFields.add(field);
              acc[cell.category] = (acc[cell.category] || 0) + 1;
            }
          });

          if (row.issues) {
            row.issues.forEach(issue => {
              if (!fieldLevelFields.has(issue.field)) {
                acc[issue.category] = (acc[issue.category] || 0) + 1;
              }
            });
          }

          return acc;
        }, {})
      : {});

  return (
    <div className="w-full bg-white border border-slate-200 rounded-2xl p-6 space-y-6 relative">
      {/* Explanation */}
      <div className="w-full">
        <div className="bg-rose-50 border border-rose-200 p-5 rounded-xl shadow-sm flex items-start gap-4">
          <DocumentTextIcon className="w-5 h-5 text-rose-700 mt-1 shrink-0" />
          <div className="text-sm text-gray-700 leading-relaxed">
            <p className="mb-1">
              This <strong>universe</strong> has been evaluated to support readiness for the{' '}
              <strong>CMS ODAG Program Audit</strong>. The analysis reflects criteria outlined in the Program Audit
              Protocol and ODAG Data Request for Part C Organization Determinations, Appeals, and Grievances.
            </p>
            <p className="mb-1">
              Each row in the universe was scanned for potential compliance issues across the following audit-critical
              elements:
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>
                <strong>Data Accuracy</strong> — Are values logically complete and valid?
              </li>
              <li>
                <strong>Field Formatting & Length</strong> — Do entries meet CMS formatting standards and length limits?
              </li>
              <li>
                <strong>Timeliness Validation</strong> — Were cases processed within the appropriate timeframe (e.g.,
                24h, 72h, 14d)?
              </li>
              <li>
                <strong>Classification Accuracy</strong> — Was each request correctly categorized (e.g., expedited vs.
                standard)?
              </li>
            </ul>
            <p className="mt-2">
              Use the filters and export tools below to review flagged records and assist with audit preparation.
            </p>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 w-full items-start">
        {/* Total Rows */}
        <div className="bg-gradient-to-r from-rose-50 to-rose-100 border border-rose-200 p-4 rounded-xl shadow-sm h-full">
          <p className="text-xs text-rose-700 font-semibold uppercase mb-1">Total Rows</p>
          <p className="text-xl font-bold text-rose-800">{totalRows}</p>
        </div>

        {/* Total Issues + Breakdown */}
        <div className="bg-gradient-to-r from-red-50 to-red-100 border border-red-200 p-4 rounded-xl shadow-sm h-full">
          <p className="text-xs text-red-700 font-semibold uppercase mb-2">Total Issues</p>
          <p className="text-xl font-bold text-red-800 mb-3">{totalIssues}</p>
          <ul className="text-sm text-red-700 space-y-1 pl-3 list-disc">
            {Object.entries(categoryCounts).map(([category, count]) => (
              <li key={category} className="flex justify-between pr-2">
                <span className="capitalize">{category}</span>
                <span className="font-semibold">{count}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Selected Category */}
        {selectedCategory && selectedCategory !== 'All' && (
          <div className="bg-gradient-to-r from-amber-50 to-amber-100 border border-amber-200 p-4 rounded-xl shadow-sm h-full">
            <p className="text-xs text-amber-700 font-semibold uppercase mb-1">Filtered Category</p>
            <p className="text-lg font-semibold text-amber-800">
              {categoryCounts[selectedCategory] || 0}
            </p>
          </div>
        )}
      </div>

      {/* Per-universe timeliness summary — CMS protocol timeliness tests
          aggregated by scenario (e.g. ODAG OD: standard non-Part B 7d,
          standard Part B 72h, expedited non-Part B 72h, expedited Part B
          24h), plus general timeliness. Counts only; renders nothing when
          the universe has no timeliness scenarios. */}
      {validationResult?.timelinessSummary?.scenarios?.length > 0 && (
        <div className="w-full bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 p-5 rounded-xl shadow-sm">
          <p className="text-xs text-emerald-800 font-semibold uppercase mb-3">
            Timeliness by category (universe-level, per CMS protocol)
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-emerald-900 border-b border-emerald-200">
                  <th className="py-1.5 pr-3 font-semibold">Scenario</th>
                  <th className="py-1.5 px-3 font-semibold text-right">Timely</th>
                  <th className="py-1.5 px-3 font-semibold text-right">Untimely</th>
                  <th className="py-1.5 px-3 font-semibold text-right">N/A</th>
                  <th className="py-1.5 pl-3 font-semibold text-right">Timeliness</th>
                </tr>
              </thead>
              <tbody>
                {validationResult.timelinessSummary.scenarios.map((s) => (
                  <tr key={s.scenario} className="border-b border-emerald-100 last:border-0">
                    <td className="py-1.5 pr-3 text-gray-800">{s.scenario}</td>
                    <td className="py-1.5 px-3 text-right text-emerald-800 font-medium">{s.timely}</td>
                    <td className={`py-1.5 px-3 text-right font-medium ${s.untimely > 0 ? 'text-red-700' : 'text-gray-500'}`}>{s.untimely}</td>
                    <td className="py-1.5 px-3 text-right text-gray-500">{s.notApplicable}</td>
                    <td className={`py-1.5 pl-3 text-right font-bold ${s.rate === null ? 'text-gray-400' : s.rate >= 95 ? 'text-emerald-700' : 'text-red-700'}`}>
                      {s.rate === null ? '—' : `${s.rate}%`}
                    </td>
                  </tr>
                ))}
                <tr className="bg-emerald-100/60">
                  <td className="py-2 pr-3 font-bold text-emerald-900">General timeliness (all categories)</td>
                  <td className="py-2 px-3 text-right font-bold text-emerald-900">{validationResult.timelinessSummary.overall.timely}</td>
                  <td className={`py-2 px-3 text-right font-bold ${validationResult.timelinessSummary.overall.untimely > 0 ? 'text-red-700' : 'text-emerald-900'}`}>{validationResult.timelinessSummary.overall.untimely}</td>
                  <td className="py-2 px-3 text-right text-gray-500">—</td>
                  <td className={`py-2 pl-3 text-right font-bold ${validationResult.timelinessSummary.overall.rate === null ? 'text-gray-400' : validationResult.timelinessSummary.overall.rate >= 95 ? 'text-emerald-700' : 'text-red-700'}`}>
                    {validationResult.timelinessSummary.overall.rate === null ? '—' : `${validationResult.timelinessSummary.overall.rate}%`}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          {(validationResult.timelinessSummary.dismissed > 0 || validationResult.timelinessSummary.indeterminate > 0) && (
            <p className="mt-2 text-xs text-gray-600">
              Excluded from timeliness per CMS protocol:{' '}
              {validationResult.timelinessSummary.dismissed > 0 && `${validationResult.timelinessSummary.dismissed} dismissed case(s)`}
              {validationResult.timelinessSummary.dismissed > 0 && validationResult.timelinessSummary.indeterminate > 0 && ' · '}
              {validationResult.timelinessSummary.indeterminate > 0 && `${validationResult.timelinessSummary.indeterminate} with unrecognized determination`}
              . N/A = rows this scenario could not be measured for (missing or invalid dates).
            </p>
          )}
        </div>
      )}

      {/* ML Error Classification */}
      <MLErrorClassification
        errors={validationResult?.errors || []}
        fileName={uploadedFile?.name || 'unknown'}
        autoTrigger={true}
      />
    </div>
  );
}
